import {
  HttpErrorResponse,
  HttpHandler,
  HttpHeaderResponse,
  HttpInterceptor,
  HttpProgressEvent,
  HttpRequest,
  HttpResponse,
  HttpSentEvent,
  HttpUserEvent,
} from '@angular/common/http';
import { ErrorHandler, Injectable, Injector, inject } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, Observable, of, throwError } from 'rxjs';
import { catchError, filter, finalize, switchMap, take } from 'rxjs/operators';
import { Globals } from '../../globals';
import { AppUserAuth } from '../../interfaces/login.interfaces';
import { AuthService } from '../../security/auth.service';
import { LogService } from '../log/log.service';
import { BadRequestError } from './bad-request-error.model';

@Injectable()
export class HttpInterceptorModule implements HttpInterceptor {
  private readonly injector = inject(Injector);
  private readonly globals = inject(Globals);

  constructor() {
    this.tokenSubject = new BehaviorSubject<string | null>(null);
  }

  isRefreshingToken = false;
  tokenSubject: BehaviorSubject<string | null>;

  /**
   * Add the access token to the header of each request
   */
  private addTokenToRequest(
    request: HttpRequest<any>,
    token: string,
  ): HttpRequest<any> {
    return request.clone({
      setHeaders: {
        Authorization: `Bearer ${token}`,
        AppOrigin: this.globals.appOrigin,
      },
    });
  }

  /**
   * Intercept all http requests
   * @param request
   * @param next
   */
  intercept(
    request: HttpRequest<any>,
    next: HttpHandler,
  ): Observable<
    | HttpSentEvent
    | HttpHeaderResponse
    | HttpProgressEvent
    | HttpResponse<any>
    | HttpUserEvent<any>
    | any
  > {
    const errorHandler = this.injector.get(ErrorHandler);
    const authService = this.injector.get(AuthService);
    const translateService = this.injector.get(TranslateService);
    const logService = this.injector.get(LogService);

    // intercept http requests
    // add the token to header request
    // handle the result when it triggered an error
    return next
      .handle(this.addTokenToRequest(request, authService.getAuthToken()))
      .pipe(
        catchError((err) => {
          if (err instanceof HttpErrorResponse) {
            switch ((<HttpErrorResponse>err).status) {
              // catch 401 to refresh token
              case 401:
                return this.handle401Error(
                  request,
                  next,
                  errorHandler,
                  authService,
                  logService,
                );

              // catch 0 and send it to the errorhandler to show offline message
              case 0:
                logService.error('HTTP INTERCEPTOR ERROR case 0 :', request);
                errorHandler.handleError(err);
                return throwError(() => err);
              case 400: {
                return this.handleBadRequestError(err, translateService);
              }
              // other errors should be passed on
              default:
                logService.error('HTTP INTERCEPTOR ERROR :', request);
                this.handleGeneralError(err);
                return throwError(() => err);
            }
          } else {
            return throwError(() => err);
          }
        }),
      );
  }

  /**
   * Handle 401 error - refresh access token
   * @param request
   * @param next
   */
  private handle401Error(
    request: HttpRequest<any>,
    next: HttpHandler,
    errorHandler: ErrorHandler,
    authService: AuthService,
    logService: LogService,
  ): Observable<any> {
    if (!this.isRefreshingToken) {
      this.isRefreshingToken = true;

      logService?.warn('HTTP-INTERCEPTOR - refreshing token');

      // Reset here so that the following requests wait until the token
      // comes back from the refreshToken call.
      this.tokenSubject.next(null);

      return authService.refreshToken().pipe(
        switchMap((user: AppUserAuth | undefined): Observable<any> => {
          // handle the result of the refresh token
          if (user) {
            this.tokenSubject.next(user.accessToken);
            localStorage.setItem('currentUser', JSON.stringify(user));
            logService?.warn(
              'HTTP-INTERCEPTOR - refreshed token redoing call',
              user.accessToken,
            );
            return next.handle(
              this.addTokenToRequest(request, user.accessToken),
            );
          }

          // if we didn't get a new token: logout
          authService.logout();
          return of(undefined);
        }),
        catchError((err) => {
          console.error('Refresh token error', err);
          logService?.error('HTTP-INTERCEPTOR - error refreshing token', err);
          //let the other requests pass
          this.tokenSubject.next('');
          // exception on calling refresh token: logout
          return <any>authService.logout();
        }),
        finalize(() => {
          this.isRefreshingToken = false;
        }),
      );
    }

    return this.tokenSubject.pipe(
      filter((token) => token !== null),
      take(1),
      switchMap((token) => {
        return next.handle(this.addTokenToRequest(request, token || ''));
      }),
    );
  }

  private handleBadRequestError(
    err: HttpErrorResponse,
    translateService: TranslateService,
  ): Observable<BadRequestError> {
    let errorMessage = err.message;
    if (err.error?.errorMessage) {
      errorMessage = err.error.errorMessage;
    } else if (err.error?.Message) {
      errorMessage = err.error.Message;
    }
    return translateService.stream(errorMessage).pipe(
      switchMap((errorMessage: string) =>
        throwError(
          () =>
            ({
              message: errorMessage,
              type: 'badRequestError',
            }) as BadRequestError,
        ),
      ),
    );
  }

  /**
   * Create a general error object and returns it
   * @param error The error that is thrown
   */
  private handleGeneralError(
    error: HttpErrorResponse,
  ): Observable<ServicesError> {
    const dataError = new ServicesError();
    dataError.message =
      error.error.message !== undefined ? error.error.message : error.message;
    return throwError(() => dataError);
  }
}

/**
 * Error object for services
 */
export class ServicesError {
  message?: string;
}
